import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Search, Building2, Network, ShoppingBag } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PORTAL_TABS = [
  { code: "ORGANIZATION", label: "Organization", icon: Building2, color: "text-emerald-300", accent: "from-emerald-500 to-teal-500" },
  { code: "DISTRIBUTOR", label: "Distributor", icon: Network, color: "text-blue-300", accent: "from-blue-500 to-indigo-500" },
  { code: "VENDOR", label: "Vendor", icon: ShoppingBag, color: "text-pink-300", accent: "from-pink-500 to-fuchsia-500" },
];

// Flatten a tree into rows that include depth + ancestor codes + sibling positions
function flatten(tree) {
  const out = [];
  const walk = (node, depth, ancestors, parentCode, isLast) => {
    out.push({
      role: node,
      depth,
      hasChildren: !!(node.children && node.children.length),
      ancestors,
      parentCode,
      isLast,
    });
    if (node.children) {
      const next = [...ancestors, node.code];
      const len = node.children.length;
      for (let i = 0; i < len; i++) walk(node.children[i], depth + 1, next, node.code, i === len - 1);
    }
  };
  for (let i = 0; i < tree.length; i++) walk(tree[i], 0, [], null, i === tree.length - 1);
  return out;
}

function rowMatchesDeep(role, search) {
  if (!search) return true;
  const s = search.toLowerCase();
  if (role.name.toLowerCase().includes(s) || role.code.toLowerCase().includes(s)) return true;
  const stack = [...(role.children || [])];
  while (stack.length) {
    const n = stack.pop();
    if (n.name.toLowerCase().includes(s) || n.code.toLowerCase().includes(s)) return true;
    if (n.children) stack.push(...n.children);
  }
  return false;
}

// Visual constants for the tree
const INDENT = 28; // px per depth level
const ROW_H = 56; // approx row height incl gap
const CONNECTOR_OFFSET = 14; // x-offset of the vertical line inside an indent column

export default function RoleHierarchyExplorer() {
  const [portal, setPortal] = useState("ORGANIZATION");
  const [tree, setTree] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${BACKEND}/api/platform/public/hierarchy?portal=${portal}`)
      .then(({ data }) => setTree(data))
      .finally(() => setLoading(false));
  }, [portal]);

  const flat = useMemo(() => flatten(tree), [tree]);

  // Visible rows = ancestor chain not collapsed AND search matches
  const visible = useMemo(() => {
    return flat.filter((item) => {
      for (let i = 0; i < item.ancestors.length; i++) {
        if (collapsed[item.ancestors[i]]) return false;
      }
      return rowMatchesDeep(item.role, search);
    });
  }, [flat, collapsed, search]);

  const toggle = (code) => setCollapsed((c) => ({ ...c, [code]: !c[code] }));

  const portalConf = PORTAL_TABS.find((t) => t.code === portal);

  return (
    <div
      className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      data-testid="hierarchy-explorer"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center p-4 border-b border-white/10">
        <div className="flex gap-1.5 bg-black/40 p-1 rounded-full border border-white/10">
          {PORTAL_TABS.map((t) => (
            <button
              key={t.code}
              onClick={() => setPortal(t.code)}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                portal === t.code ? "bg-white text-black" : `${t.color} hover:bg-white/5`
              }`}
              data-testid={`hierarchy-tab-${t.code.toLowerCase()}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-full bg-black/40 border border-white/10">
          <Search className="w-4 h-4 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search roles…"
            className="bg-transparent outline-none text-sm w-full text-white placeholder-white/30"
            data-testid="hierarchy-search"
          />
        </div>
      </div>

      {/* Tree */}
      <div className="p-6 max-h-[600px] overflow-auto">
        {loading ? <div className="text-white/50 text-sm">Loading hierarchy…</div> : null}
        {!loading && visible.length === 0 ? <div className="text-white/50 text-sm">No roles match.</div> : null}

        <div className="relative">
          {visible.map((item, rowIdx) => {
            const isCollapsed = !!collapsed[item.role.code];
            const isParent = item.hasChildren;

            // Build the indent + connector columns
            const lanes = [];
            for (let d = 0; d < item.depth; d++) {
              const ancestorCode = item.ancestors[d];
              // is the row at this ancestor depth the "last child"? Look up by walking flat
              // Simpler: render a vertical line if there is any visible row later that shares this ancestor
              const hasMoreSiblings = visible.slice(rowIdx + 1).some(
                (later) => later.ancestors[d] === ancestorCode && later.depth >= d + 1
              );
              lanes.push(hasMoreSiblings ? "line" : "empty");
            }

            return (
              <div
                key={item.role.code}
                className="relative flex items-stretch group"
                style={{ minHeight: ROW_H }}
                data-testid={`role-node-${item.role.code}`}
              >
                {/* Indent + connector lanes */}
                {lanes.map((lane, i) => (
                  <div key={i} className="relative" style={{ width: INDENT, flexShrink: 0 }}>
                    {lane === "line" ? (
                      <span
                        className="absolute top-0 bottom-0 w-px bg-white/15"
                        style={{ left: CONNECTOR_OFFSET }}
                      />
                    ) : null}
                  </div>
                ))}

                {/* Elbow connector for this row (only if depth > 0) */}
                {item.depth > 0 ? (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: (item.depth - 1) * INDENT + CONNECTOR_OFFSET,
                      top: 0,
                      width: INDENT - CONNECTOR_OFFSET + 8,
                      height: 28,
                    }}
                  >
                    {/* vertical to mid */}
                    <span
                      className="absolute left-0 top-0 w-px bg-white/25"
                      style={{ height: item.isLast ? 28 : "100%" }}
                    />
                    {/* horizontal to node */}
                    <span
                      className="absolute h-px bg-white/25"
                      style={{ left: 0, top: 28, width: "100%" }}
                    />
                  </div>
                ) : null}

                {/* The node card */}
                <div className="flex-1 py-2">
                  <button
                    onClick={() => isParent && toggle(item.role.code)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all ${
                      item.depth === 0
                        ? `border-white/15 bg-gradient-to-r ${portalConf?.accent || "from-white/10 to-white/5"} bg-opacity-20 shadow-lg shadow-black/30`
                        : "border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]"
                    }`}
                    data-testid={`role-btn-${item.role.code}`}
                  >
                    {/* Caret */}
                    <span className="w-5 flex items-center justify-center">
                      {isParent ? (
                        isCollapsed ? (
                          <ChevronRight className="w-4 h-4 text-white/60" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-white/80" />
                        )
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
                      )}
                    </span>

                    {/* Node body */}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-bold truncate ${item.depth === 0 ? "text-white" : "text-white/95"}`}>
                        {item.role.name}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40 font-mono truncate">
                        {item.role.code}
                      </div>
                    </div>

                    {/* Right meta */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {item.role.base_cost_per_user > 0 ? (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-white/70">
                          ₹{item.role.base_cost_per_user}/u
                        </span>
                      ) : null}
                      {isParent ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300">
                          {item.role.children.length}
                        </span>
                      ) : null}
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer counts */}
      <div className="px-6 py-3 border-t border-white/10 text-[11px] text-white/50 flex items-center justify-between">
        <span>{flat.length} roles · {flat.filter((r) => r.hasChildren).length} parent nodes</span>
        <span>Click a node to expand / collapse</span>
      </div>
    </div>
  );
}
